import { Grid, styled } from '@mui/material';
import TruncatedTypographicText from 'src/components/truncated-typographic-text';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { formatNumber } from '../../../pricing-simulator';
import { toggleDrawerView } from '../../../planner/store/planner-slice';
import DRAWER_VIEW_TYPE from '../../../planner/types/drawer-view-type';
import { ProductMetric } from '../../../planner/types/ppg-sku-drawer.model';
import MetricsPopper from './metrics-popper';
import usePopper from '../../../planner/hooks/popper-hook';
import { IColorGradientMapDetails } from '../../types/grid-color-gradient-map.model';

interface IProductProps {
  /**
   * Name of the product
   */
  name: string;
  /**
   * view to be passed to the drawer (PPG or SKU)
   */
  view: DRAWER_VIEW_TYPE;
  /**
   * The id of the product passed to drawer when opened
   */
  productId: number;
  /**
   * Is the product marked for opportunities
   */
  isMarkedForOpportunity: boolean;
  /**
   * Gradient value nto define the gradient opacity
   */
  gradientValue?: number;
}

interface IGridDatumBoxProps extends IProductProps {
  /**
   * Details of the price of the product
   */
  price: ProductMetric & { value?: number };
  /**
   * Opportunities selected for the product
   */
  opportunities: number[];
  /**
   * Metrics of the product
   */
  metrics: (ProductMetric & { value?: number })[];

  // eslint-disable-next-line react/require-default-props
  gradientMap?: IColorGradientMapDetails;
  /**
   * Image of the product
   */
  imgUrl: string;
}

const StyledDiv = styled('div')(() => ({
  borderRadius: '50%',
  backgroundSize: 'cover',
  height: '30px',
  width: '30px',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  border: '0.72px solid #8080CF'
}));

/**
 * Renders the image for the PPG
 */
function ItemImage({
  price,
  imgUrl
}: {
  price: IGridDatumBoxProps['price'];
  imgUrl: string;
}) {
  return (
    <Grid item xs='auto'>
      <Grid>
        <StyledDiv
          sx={{
            border: '0.72px solid #8080CF',
            backgroundColor: 'white',
            borderRadius: '50%'
          }}
          style={{
            backgroundImage: `url(${imgUrl || '/static/images/logo/apple-touch-icon-120x120.png'
              })`
          }}
        />
      </Grid>
      {price === null ? null : (
        <Grid
          sx={{
            backgroundColor: '#E6E6F6',
            color: '#8080CF',
            fontWeight: 700,
            textAlign: 'center',
            fontSize: '8px',
            borderRadius: (theme) => theme.spacing(1),
            padding: (theme) => theme.spacing(0.25, 1)
          }}
        >
          {formatNumber(
            price.metric_format,
            price.value,
            price.prefix,
            price.suffix
          )}
        </Grid>
      )}
    </Grid>
  );
}

/**
 * Renders the name of the product (PPG)
 * Attaches a click event to open drawer on click but only in case the `view` is PPG
 */
function PPGName({
  name,
  view,
  productId,
  gradientValue,
  isMarkedForOpportunity = false,

}: IProductProps) {
  const appDispatch = useAppDispatch();
  return (
    <TruncatedTypographicText
      fontSize='10px'
      fontWeight={
        view === DRAWER_VIEW_TYPE.PPG && !isMarkedForOpportunity ? 400 : 700
      }
      textTransform='uppercase'
      color={isMarkedForOpportunity || gradientValue ? 'black' : '#777777'}
      sx={
        DRAWER_VIEW_TYPE.PPG === view && productId > -1
          ? {
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
              color: (theme) => theme.palette.primary.main
            }
          }
          : {}
      }
      onClick={() => {
        if (view === DRAWER_VIEW_TYPE.PPG && productId > -1) {
          appDispatch(
            toggleDrawerView({
              view,
              productId,
              ppgId: productId
            })
          );
        }
      }}
    >
      {name}
    </TruncatedTypographicText>
  );
}

function PPGOpportunityGrid({ opportunities }: { opportunities: number[] }) {
  const { opportunityToColorMap } = useAppSelector(
    (state) => state.simulatorHome
  );
  return (
    <Grid
      item
      sx={{
        width: '10px',
        borderRadius: '5px',
        backgroundColor: '#E8E8E8',
        mx: 1,
        height: '50px',
        overflow: 'hidden'
      }}
    >
      {Array.isArray(opportunities) && opportunities.length
        ? opportunities.map((opportunityId) => (
          <Grid
            key={opportunityId}
            sx={{
              backgroundColor:
                opportunityToColorMap.find(
                  (opp) => opp.pk_id === opportunityId
                )?.color || '#E8E8E8',
              height: `${(100 / opportunities.length / 100) * 50}px`
            }}
          />
        ))
        : null}
    </Grid>
  );
}

/**
 * Renders the image and name of the product (PPG) in grid cell
 */
export default function PPGDetails({
  productId,
  view,
  price,
  name,
  opportunities,
  metrics,
  gradientMap,
  imgUrl,
  isMarkedForOpportunity = false
}: IGridDatumBoxProps) {
  const [gradientValue] = gradientMap
    ? Object.keys(gradientMap.mac_mapping)
      .filter((key) => Number(key) === productId)
      .map((key) => gradientMap.mac_mapping[key])
    : [null];
  const { handleMouseEnter, handleMouseLeave, anchorEl, isPopperOpen } =
    usePopper({ showPopper: productId > -1 });
  const { opportunityToColorMap } = useAppSelector(
    (state) => state.simulatorHome
  );
  const delisOpp = opportunityToColorMap.find((opp) => opp.value === 'delist');
  return (
    <Grid flexWrap='nowrap' mb={1} item xs={12} container>
      <Grid item xs='auto'>
        <ItemImage
          price={price}
          imgUrl={imgUrl || '/static/images/logo/apple-touch-icon-120x120.png'}
        />
      </Grid>
      {view === DRAWER_VIEW_TYPE.PPG ? (
        <PPGOpportunityGrid opportunities={opportunities} />
      ) : null}
      <Grid
        item
        xs
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          backgroundColor:
            gradientValue > 0
              ? `rgba(0, 178, 47, ${gradientValue / gradientMap.max})`
              : gradientValue < 0
                ? `rgba(255, 134, 131, ${gradientValue / gradientMap.min})`
                : gradientValue === 0 && opportunities.includes(delisOpp?.pk_id)
                  ? 'rgba(255, 134, 131, 1)'
                  : 'transparent',
          borderRadius: 1,
          padding: '4px'
        }}
      >
        <PPGName
          isMarkedForOpportunity={isMarkedForOpportunity}
          view={view}
          name={name}
          gradientValue={gradientValue}
          productId={productId}
        />
      </Grid>
      {metrics.length ? (
        <MetricsPopper
          metrics={metrics}
          open={isPopperOpen}
          anchorEl={anchorEl}
        />
      ) : null}
    </Grid>
  );
}
